import { useState, useEffect, useCallback, useRef } from 'react';
import { OpenAPI } from '@rbx/docsite-utilities/dist/browser';
import useOpenAPIDocument from './useOpenAPIDocument';

/**
 * Custom hook for managing individual operation expansion state
 * Handles URL fragment synchronization and prevents unnecessary re-renders
 */
export const useOperationExpansion = (
  operation: OpenAPI.OperationObject,
  method: OpenAPI.HttpMethod,
  path: string,
) => {
  const document = useOpenAPIDocument();
  const [isExpanded, setIsExpanded] = useState(false);
  const isInitialized = useRef(false);
  const operationSlug = OpenAPI.getOperationDeepLinkFragment(document, operation, method, path);

  // Handle URL fragment changes
  useEffect(() => {
    const handleFragmentChange = () => {
      const currentFragment = window.location.hash.slice(1);
      const shouldExpand = currentFragment === operationSlug;

      // Only update if this operation's state would actually change
      if (shouldExpand !== isExpanded) {
        setIsExpanded(shouldExpand);
      }
    };

    // Initial check
    if (!isInitialized.current) {
      handleFragmentChange();
      isInitialized.current = true;
    }

    // Listen for fragment changes
    window.addEventListener('hashchange', handleFragmentChange);

    return () => {
      window.removeEventListener('hashchange', handleFragmentChange);
    };
  }, [operationSlug, isExpanded]);

  // Handle operation click
  const handleToggle = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const newExpanded = !isExpanded;
      setIsExpanded(newExpanded);

      // Update URL fragment
      if (newExpanded) {
        window.history.pushState(
          null,
          '',
          `${window.location.pathname + window.location.search}#${operationSlug}`,
        );
      } else {
        window.history.pushState(null, '', `${window.location.pathname + window.location.search}`);
      }

      // Trigger hashchange event to notify other operations
      const hashChangeEvent = new Event('hashchange');
      window.dispatchEvent(hashChangeEvent);
    },
    [isExpanded, operationSlug],
  );

  return {
    isExpanded,
    handleToggle,
    operationSlug,
  };
};

export default useOperationExpansion;
